/**
 * useChunkedShiftData - Manages loading shift data in monthly chunks for infinite scroll
 *
 * This hook orchestrates the loading of shift data in monthly chunks as the user scrolls.
 * It uses the existing useShiftsQuery hook internally for each chunk, providing:
 * - Automatic chunk loading based on visible range
 * - 1-month buffer loading ahead of scroll direction
 * - Tracking of loaded/loading state per month
 * - Combined shift data for virtualization
 *
 * Memory management is handled by TanStack Query's gcTime - old chunks are automatically
 * garbage collected when not accessed for 30 minutes.
 */

import { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import { useQuery, useQueries, useQueryClient } from '@tanstack/react-query';
import {
  startOfMonth,
  endOfMonth,
  addMonths,
  subMonths,
  getMonth,
  getYear,
  isSameMonth,
  isAfter,
  isBefore,
  startOfDay
} from 'date-fns';
import { UnifiedShiftService, UnifiedShiftQueryResult } from '@/lib/scheduling/unified-shift-service';
import { DayShift, Employee } from '@/types';
import { shiftQueryKeys } from './useShiftsQuery';
import { getMonthKey, parseMonthKey } from './useInfiniteScrollDateRange';

export interface ChunkLoadingState {
  /** Month key in format "YYYY-MM" */
  monthKey: string;
  /** Whether this chunk is currently loading */
  isLoading: boolean;
  /** Whether this chunk has been loaded */
  isLoaded: boolean;
  /** Whether this chunk failed to load */
  hasError: boolean;
  /** Last fetch timestamp */
  fetchedAt?: number;
}

export interface UseChunkedShiftDataOptions {
  /** Business ID to fetch shifts for */
  businessId: string | null;
  /** Array of employees for shift enrichment */
  employees: Employee[];
  /** Start date of the infinite scroll range */
  rangeStartDate: Date;
  /** End date of the infinite scroll range */
  rangeEndDate: Date;
  /** Whether chunk loading is enabled */
  enabled?: boolean;
  /** Number of buffer months to load ahead/behind visible range */
  bufferMonths?: number;
  /** Debounce time for visible range changes (ms) */
  debounceMs?: number;
}

export interface UseChunkedShiftDataResult {
  /** All loaded shifts combined */
  shifts: DayShift[];
  /** Map of shifts by day (for quick lookup) */
  shiftsByDay: Map<string, DayShift[]>;
  /** Loading state for each chunk */
  chunkStates: Map<string, ChunkLoadingState>;
  /** Whether any chunk is currently loading */
  isAnyChunkLoading: boolean;
  /** Set the visible date range (triggers chunk loading) */
  setVisibleRange: (startIndex: number, endIndex: number, allDays: Date[]) => void;
  /** Force reload a specific month */
  reloadMonth: (year: number, month: number) => void;
  /** Get loading state for a specific day index */
  isDayLoading: (dayIndex: number, allDays: Date[]) => boolean;
  /** Prefetch a specific month */
  prefetchMonth: (year: number, month: number) => void;
}

/**
 * Hook for managing chunked shift data loading in infinite scroll calendar
 */
export function useChunkedShiftData({
  businessId,
  employees,
  rangeStartDate,
  rangeEndDate,
  enabled = true,
  bufferMonths = 1,
  debounceMs = 150
}: UseChunkedShiftDataOptions): UseChunkedShiftDataResult {
  const queryClient = useQueryClient();

  // Track which months should be loaded based on visible range
  const [monthsToLoad, setMonthsToLoad] = useState<Set<string>>(new Set());

  // Debounce timer ref
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);

  // Track last scroll direction for buffer loading
  const lastVisibleRangeRef = useRef<{ start: number; end: number } | null>(null);

  // Calculate all months in the range that could potentially be loaded
  const allMonthsInRange = useMemo(() => {
    if (!enabled) return [];

    const months: string[] = [];
    let currentMonth = startOfMonth(rangeStartDate);
    const endMonth = startOfMonth(rangeEndDate);

    while (isBefore(currentMonth, endMonth) || isSameMonth(currentMonth, endMonth)) {
      months.push(getMonthKey(currentMonth));
      currentMonth = addMonths(currentMonth, 1);
    }

    return months;
  }, [rangeStartDate, rangeEndDate, enabled]);

  // Initialize with current month on mount
  useEffect(() => {
    if (enabled && monthsToLoad.size === 0) {
      const today = new Date();
      const currentMonthKey = getMonthKey(today);
      setMonthsToLoad(new Set([currentMonthKey]));
    }
  }, [enabled]);

  // Set visible range - debounced to avoid too many chunk loading triggers
  const setVisibleRange = useCallback((startIndex: number, endIndex: number, allDays: Date[]) => {
    if (!enabled || allDays.length === 0) return;

    // Clear existing debounce timer
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    debounceTimerRef.current = setTimeout(() => {
      // Get dates at visible indices
      const startDate = allDays[Math.max(0, startIndex)];
      const endDate = allDays[Math.min(allDays.length - 1, endIndex)];

      if (!startDate || !endDate) return;

      // Calculate months that need to be loaded
      const monthsNeeded = new Set<string>();

      // Add visible months
      let currentMonth = startOfMonth(startDate);
      const visibleEndMonth = startOfMonth(endDate);

      while (isBefore(currentMonth, visibleEndMonth) || isSameMonth(currentMonth, visibleEndMonth)) {
        monthsNeeded.add(getMonthKey(currentMonth));
        currentMonth = addMonths(currentMonth, 1);
      }

      // Detect scroll direction and add buffer months
      const lastRange = lastVisibleRangeRef.current;
      if (lastRange) {
        const scrollingForward = startIndex > lastRange.start;
        const scrollingBackward = startIndex < lastRange.start;

        // Add buffer in scroll direction
        if (scrollingForward) {
          // Add months after the visible range
          for (let i = 1; i <= bufferMonths; i++) {
            const bufferMonth = addMonths(visibleEndMonth, i);
            const bufferKey = getMonthKey(bufferMonth);
            if (allMonthsInRange.includes(bufferKey)) {
              monthsNeeded.add(bufferKey);
            }
          }
        } else if (scrollingBackward) {
          // Add months before the visible range
          for (let i = 1; i <= bufferMonths; i++) {
            const bufferMonth = subMonths(startOfMonth(startDate), i);
            const bufferKey = getMonthKey(bufferMonth);
            if (allMonthsInRange.includes(bufferKey)) {
              monthsNeeded.add(bufferKey);
            }
          }
        }
      } else {
        // Initial load - add buffer on both sides
        for (let i = 1; i <= bufferMonths; i++) {
          const nextMonth = addMonths(visibleEndMonth, i);
          const prevMonth = subMonths(startOfMonth(startDate), i);

          const nextKey = getMonthKey(nextMonth);
          const prevKey = getMonthKey(prevMonth);

          if (allMonthsInRange.includes(nextKey)) monthsNeeded.add(nextKey);
          if (allMonthsInRange.includes(prevKey)) monthsNeeded.add(prevKey);
        }
      }

      // Update last range ref
      lastVisibleRangeRef.current = { start: startIndex, end: endIndex };

      // Update months to load (merge with existing to keep already loaded data)
      setMonthsToLoad(prev => {
        const newSet = new Set(prev);
        monthsNeeded.forEach(m => newSet.add(m));
        return newSet;
      });
    }, debounceMs);
  }, [enabled, bufferMonths, debounceMs, allMonthsInRange]);

  // Create queries for each month that needs to be loaded
  const monthQueries = useQueries({
    queries: Array.from(monthsToLoad).map(monthKey => {
      const { year, month } = parseMonthKey(monthKey);
      const monthStart = new Date(year, month, 1);
      const monthEnd = endOfMonth(monthStart);

      return {
        queryKey: shiftQueryKeys.list(
          businessId || '',
          monthStart.toISOString(),
          monthEnd.toISOString()
        ),
        queryFn: async (): Promise<UnifiedShiftQueryResult> => {
          if (!businessId) {
            return {
              shifts: [],
              totalCount: 0,
              historicalCount: 0,
              currentCount: 0,
              performanceStats: {
                storedStatusCount: 0,
                realTimeCalculatedCount: 0,
                cacheHitRate: 0,
              },
            };
          }

          return UnifiedShiftService.fetchShiftsForDateRange(
            businessId,
            monthStart,
            monthEnd,
            employees,
            false
          );
        },
        enabled: enabled && !!businessId && monthsToLoad.has(monthKey),
        staleTime: 1000 * 60 * 5, // 5 minutes
        gcTime: 1000 * 60 * 30, // 30 minutes
        refetchOnWindowFocus: false,
        refetchOnMount: false,
      };
    }),
  });

  // Build chunk states map
  const chunkStates = useMemo(() => {
    const states = new Map<string, ChunkLoadingState>();
    const monthKeys = Array.from(monthsToLoad);

    monthQueries.forEach((query, index) => {
      const monthKey = monthKeys[index];
      if (monthKey) {
        states.set(monthKey, {
          monthKey,
          isLoading: query.isLoading,
          isLoaded: query.isSuccess && !query.isLoading,
          hasError: query.isError,
          fetchedAt: query.dataUpdatedAt || undefined,
        });
      }
    });

    return states;
  }, [monthQueries, monthsToLoad]);

  // Combine all loaded shifts
  const shifts = useMemo(() => {
    const allShifts: DayShift[] = [];

    monthQueries.forEach(query => {
      if (query.data?.shifts) {
        allShifts.push(...query.data.shifts);
      }
    });

    // Sort by start time for consistent ordering
    return allShifts.sort((a, b) => a.startTime.getTime() - b.startTime.getTime());
  }, [monthQueries]);

  // Build shifts by day map for quick lookup
  const shiftsByDay = useMemo(() => {
    const map = new Map<string, DayShift[]>();

    shifts.forEach(shift => {
      const dayKey = startOfDay(shift.startTime).toISOString();
      const existing = map.get(dayKey) || [];
      existing.push(shift);
      map.set(dayKey, existing);
    });

    return map;
  }, [shifts]);

  // Check if any chunk is loading
  const isAnyChunkLoading = useMemo(() => {
    return monthQueries.some(q => q.isLoading);
  }, [monthQueries]);

  // Check if a specific day is in a loading chunk
  const isDayLoading = useCallback((dayIndex: number, allDays: Date[]): boolean => {
    if (!enabled || dayIndex < 0 || dayIndex >= allDays.length) {
      return false;
    }

    const date = allDays[dayIndex];
    const monthKey = getMonthKey(date);
    const state = chunkStates.get(monthKey);

    // If month isn't in our load set yet, consider it loading
    if (!state) {
      return monthsToLoad.has(monthKey);
    }

    return state.isLoading;
  }, [enabled, chunkStates, monthsToLoad]);

  // Force reload a specific month
  const reloadMonth = useCallback((year: number, month: number) => {
    const monthStart = new Date(year, month, 1);
    const monthEnd = endOfMonth(monthStart);

    queryClient.invalidateQueries({
      queryKey: shiftQueryKeys.list(
        businessId || '',
        monthStart.toISOString(),
        monthEnd.toISOString()
      ),
    });
  }, [businessId, queryClient]);

  // Prefetch a specific month
  const prefetchMonth = useCallback((year: number, month: number) => {
    const monthKey = `${year}-${String(month + 1).padStart(2, '0')}`;

    // Add to months to load if not already there
    setMonthsToLoad(prev => {
      if (prev.has(monthKey)) return prev;
      const newSet = new Set(prev);
      newSet.add(monthKey);
      return newSet;
    });
  }, []);

  // Cleanup debounce timer on unmount
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  return {
    shifts,
    shiftsByDay,
    chunkStates,
    isAnyChunkLoading,
    setVisibleRange,
    reloadMonth,
    isDayLoading,
    prefetchMonth,
  };
}

/**
 * Hook to get shifts for a specific day from chunked data
 * Returns empty array if day's chunk isn't loaded yet
 */
export function useShiftsForDayFromChunks(
  date: Date,
  shiftsByDay: Map<string, DayShift[]>
): DayShift[] {
  const dayKey = startOfDay(date).toISOString();
  return shiftsByDay.get(dayKey) || [];
}
